import { useTranslations } from "@/lib/useTranslations";
import type { ReactNode } from "react";
import { useState, useTransition } from "react";

import { updateArticleStatus } from "@/api/articles";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { FieldLabel } from "@/components/ui/FieldLabel";
import type { ArticleEditStatusValue, ArticleEditor } from "@/core/services/types/article.types";
import {
  getMinimumScheduleDateInputValue,
  toDateInputValue,
} from "@/core/services/types/article.types";

const dateInputClassName =
  "w-full wireframe-border bg-neutral-50 px-3 py-2 text-sm text-whart-text outline-none focus:border-whart-dark disabled:opacity-50";

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="text-xs text-whart-urgent">{message}</p>;
}

interface ArticleScheduleFieldProps {
  articleId: string;
  savedStatus: ArticleEditStatusValue;
  publishedAt: string | null;
  disabled: boolean;
  publishDisabled: boolean;
  saveSpinner: ReactNode;
  onSuccess: (article: ArticleEditor) => void;
}

export function ArticleScheduleField({
  articleId,
  savedStatus,
  publishedAt,
  disabled,
  publishDisabled,
  saveSpinner,
  onSuccess,
}: ArticleScheduleFieldProps) {
  const t = useTranslations("articles.edit");
  const tValidation = useTranslations("articles.edit.validation");
  const tCommon = useTranslations("common");
  const [isPending, startTransition] = useTransition();
  const [open, setOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [fieldError, setFieldError] = useState<string | undefined>();

  const canSchedule =
    savedStatus === "DRAFT" ||
    savedStatus === "PENDING_REVIEW" ||
    savedStatus === "SCHEDULED";

  if (!canSchedule) return null;

  const isEditingSchedule = savedStatus === "SCHEDULED";

  function openDialog() {
    setScheduleDate(isEditingSchedule ? toDateInputValue(publishedAt) : "");
    setError(undefined);
    setFieldError(undefined);
    setOpen(true);
  }

  function closeDialog() {
    if (!isPending) {
      setOpen(false);
      setError(undefined);
      setFieldError(undefined);
    }
  }

  function handleSubmit() {
    setError(undefined);
    setFieldError(undefined);

    const formData = new FormData();
    formData.set("id", articleId);
    formData.set("edit_status", "SCHEDULED");
    formData.set("published_at", scheduleDate);

    startTransition(async () => {
      const result = await updateArticleStatus({ success: false }, formData);

      if (!result.success) {
        setError(result.error);
        setFieldError(result.fieldErrors?.published_at);
        return;
      }

      onSuccess(result.data);
      setOpen(false);
    });
  }

  return (
    <>
      <Button
        type="button"
        variant="secondary"
        disabled={disabled || publishDisabled}
        title={publishDisabled ? t("publishChecklistIncomplete") : undefined}
        onClick={openDialog}
        className="inline-flex w-full items-center justify-center gap-2"
      >
        {isEditingSchedule ? t("editScheduleButton") : t("scheduleButton")}
      </Button>

      <Dialog
        open={open}
        onClose={closeDialog}
        title={isEditingSchedule ? t("editScheduleDialogTitle") : t("scheduleLabel")}
      >
        <div className="space-y-4">
          <p className="text-xs text-neutral-600">{t("scheduleCronHint")}</p>

          <div className="space-y-2">
            <FieldLabel htmlFor="article-schedule-date">
              {t("scheduledForLabel")}
            </FieldLabel>
            <input
              id="article-schedule-date"
              type="date"
              value={scheduleDate}
              min={getMinimumScheduleDateInputValue()}
              disabled={isPending}
              onChange={(event) => {
                setScheduleDate(event.target.value);
              }}
              className={dateInputClassName}
            />
            <FieldError message={fieldError} />
          </div>

          {error ? (
            <p role="alert" className="text-xs text-whart-urgent">
              {error}
            </p>
          ) : null}

          {publishDisabled ? (
            <p className="text-xs text-neutral-500">{t("publishChecklistIncomplete")}</p>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={closeDialog}
              disabled={isPending}
            >
              {tCommon("cancel")}
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={isPending || publishDisabled || !scheduleDate.trim()}
              aria-busy={isPending}
              onClick={handleSubmit}
              className="inline-flex items-center gap-2"
            >
              {isPending ? saveSpinner : null}
              {isPending
                ? tValidation("savingSchedule")
                : isEditingSchedule
                  ? t("saveDate")
                  : t("scheduleSubmit")}
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
